import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand, ThemeToggle, StatusBadge, TeamAvatar } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Plus, LogOut, Users, Swords, ArrowRight } from "lucide-react";

export default function HostDashboard() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tournaments").then((r) => setTournaments(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b bg-background/70 px-5 py-3 backdrop-blur-xl">
        <Brand onClick={() => nav("/")} />
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button variant="ghost" size="icon" onClick={() => { logout(); nav("/"); }} data-testid="logout-button" aria-label="Log out">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-label text-primary">Host dashboard</div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
              Hi {user?.name?.split(" ")[0] || "there"} 👋
            </h1>
            <p className="mt-1 text-muted-foreground">{tournaments.length} tournament{tournaments.length !== 1 ? "s" : ""}</p>
          </div>
          <Button size="lg" onClick={() => nav("/host/new")} data-testid="create-tournament-button">
            <Plus className="mr-2 h-4 w-4" /> New tournament
          </Button>
        </div>

        {loading ? (
          <p className="mt-16 text-center text-muted-foreground">Loading…</p>
        ) : tournaments.length === 0 ? (
          <Card className="mt-10 flex flex-col items-center gap-4 p-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Swords className="h-7 w-7" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-bold">No tournaments yet</h3>
              <p className="text-sm text-muted-foreground">Create your first tournament in a few minutes.</p>
            </div>
            <Button onClick={() => nav("/host/new")} data-testid="empty-create-button">
              <Plus className="mr-2 h-4 w-4" /> Create tournament
            </Button>
          </Card>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {tournaments.map((t) => (
              <Card key={t.id} className="card-lift cursor-pointer overflow-hidden" onClick={() => nav(`/host/t/${t.id}`)}
                data-testid={`tournament-card-${t.id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <TeamAvatar team={{ name: t.name, logo_url: t.logo_url }} size={44} />
                    <StatusBadge status={t.status} />
                  </div>
                  <h3 className="mt-3 font-heading text-lg font-bold tracking-tight">{t.name}</h3>
                  <p className="text-sm text-muted-foreground">{t.location || "No location"}</p>
                  <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {t.team_count}/{t.max_teams}</span>
                    <span className="flex items-center gap-1"><Swords className="h-4 w-4" /> {t.match_count}</span>
                    <ArrowRight className="ml-auto h-4 w-4" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
